import { Context, useContext } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export type Store<T> = {
    subscribeToId: (id: string, callback: () => void) => () => void;
    subscribeToAll: (callback: () => void) => () => void;
    getSnapshot: (id: string) => T extends boolean ? T : T | null;
    setValue: (id: string, value: T | null) => void;
    getAll: () => Map<string, T>;
    setAll: (value: Map<string, T>, merge?: boolean) => void;
};

type ProviderFields<T> = Pick<
    Store<T>,
    'subscribeToId' | 'subscribeToAll' | 'getSnapshot' | 'getAll'
>;

export type ProviderStore<T> = Partial<Store<T>> & ProviderFields<T>;
export type ProviderWritableStore<T> = ProviderStore<T> & Pick<Store<T>, 'setValue'>;

export const createDefaultProviderStore = <T>(initialValue: T): ProviderStore<T> => ({
    getSnapshot: () => initialValue as T extends boolean ? T : T | null,
    subscribeToId: () => () => {
        // Do Nothing
    },
    subscribeToAll: () => () => {
        // Do Nothing
    },
    getAll: () => new Map(),
});

export const createDefaultWritableProviderStore = <T>(
    initialValue: T,
): ProviderWritableStore<T> => ({
    ...createDefaultProviderStore(initialValue),
    setValue: () => {
        // Do Nothing
    },
});

export const createStore = <T>(): Store<T> => {
    const values = new Map<string, T>();
    const listeners = new Map<string, Set<() => void>>();
    const globalListeners = new Set<() => void>();

    const getListener = (id: string) => {
        const currentListener = listeners.get(id);
        if (!currentListener) {
            const set = new Set<() => void>();
            listeners.set(id, set);
            return set;
        }
        return currentListener;
    };

    const subscribeToId = (id: string, cb: () => void) => {
        const listener = getListener(id);
        listener.add(cb);

        return () => {
            listener.delete(cb);
        };
    };

    const subscribeToAll = (cb: () => void) => {
        globalListeners.add(cb);
        return () => {
            globalListeners.delete(cb);
        };
    };

    const setValue = (id: string, value: T | null) => {
        if (value !== null) {
            values.set(id, value);
        } else {
            values.delete(id);
        }

        listeners.get(id)?.forEach((callback) => callback());
        globalListeners.forEach((callback) => callback());
    };

    const getSnapshot = (id: string) => {
        const snapshot = values.get(id) ?? null;
        return snapshot as T extends boolean ? T : T | null;
    };

    const getAll = () => values;

    const setAll = (newValues: Map<string, T>, merge?: boolean) => {
        if (!merge) {
            values.clear();
        }

        newValues.forEach((value, id) => {
            setValue(id, value);
        });
    };

    return {
        subscribeToId,
        subscribeToAll,
        setValue,
        getSnapshot,
        getAll,
        setAll,
    };
};

type StoreValue<S> = S extends { getAll: () => Map<string, infer T> } ? T : never;

export const useStoreValue = <V, U extends keyof V>(context: Context<V>, key: U, id: string) => {
    const store = useContext(context)[key] as ProviderStore<StoreValue<V[U]>>;
    const { subscribeToId, getSnapshot } = store;

    return useSyncExternalStore(
        (onStoreChange) => subscribeToId(id, onStoreChange),
        () => getSnapshot(id),
    );
};
